import { TransportError } from 'ProdantixSdk/core/errors';
import { type FetchLike, makeAbortController, resolveFetch, trimTrailingSlash } from 'ProdantixSdk/core/http';
import type { InboxMessage, InboxResponse } from 'ProdantixSdk/core/types';

export interface MessagesClientOptions {
  fetch?: FetchLike;
  host: string;
  projectKey: string;
  requestTimeoutMs?: number;
}

export class MessagesClient {
  private readonly fetch?: FetchLike;
  private readonly host: string;
  private readonly projectKey: string;
  private readonly requestTimeoutMs: number;

  constructor(options: MessagesClientOptions) {
    this.fetch = resolveFetch(options.fetch);
    this.host = trimTrailingSlash(options.host);
    this.projectKey = options.projectKey;
    this.requestTimeoutMs = options.requestTimeoutMs ?? 10_000;
  }

  async inbox(distinctId: string): Promise<InboxMessage[]> {
    const fetchImpl = this.requireFetch();
    const url = `${this.host}/v1/messages?distinct_id=${encodeURIComponent(distinctId)}`;
    const response = await this.request(fetchImpl, url, { headers: this.authHeaders(), method: 'GET' });
    if (!response.ok) {
      throw new TransportError(`inbox request failed with status ${response.status}`, response.status);
    }
    const parsed = JSON.parse(await response.text()) as InboxResponse;
    return parsed.messages ?? [];
  }

  async markRead(messageId: string, distinctId: string): Promise<void> {
    const fetchImpl = this.requireFetch();
    const url = `${this.host}/v1/messages/read`;
    const response = await this.request(fetchImpl, url, {
      body: JSON.stringify({ distinct_id: distinctId, message_id: messageId }),
      headers: { ...this.authHeaders(), 'content-type': 'application/json' },
      method: 'POST',
    });
    if (!response.ok) {
      throw new TransportError(`mark-read request failed with status ${response.status}`, response.status);
    }
  }

  private requireFetch(): FetchLike {
    if (!this.fetch) {
      throw new TransportError('no fetch implementation available in this runtime');
    }
    return this.fetch;
  }

  private authHeaders(): Record<string, string> {
    return { authorization: `Bearer ${this.projectKey}` };
  }

  private async request(fetchImpl: FetchLike, url: string, init: { body?: string; headers: Record<string, string>; method: string }): ReturnType<FetchLike> {
    const controller = makeAbortController();
    const timer = controller ? setTimeout(() => controller.abort(), this.requestTimeoutMs) : undefined;
    try {
      return await fetchImpl(url, { ...init, signal: controller?.signal });
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    }
  }
}
